import { create } from 'zustand';

export type ToastTone = 'default' | 'success' | 'error';

export interface ToastAction {
  label: string;
  onClick: () => void | Promise<void>;
}

export interface ToastItem {
  id: string;
  message: string;
  tone: ToastTone;
  /** Optional bold title above the message; falls back to a tone default. */
  title?: string;
  action?: ToastAction;
}

interface ToastState {
  toasts: ToastItem[];
  push: (message: string, tone: ToastTone, title?: string, action?: ToastAction) => string;
  dismiss: (id: string) => void;
}

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  push: (message, tone, title, action) => {
    const id = crypto.randomUUID();
    set((state) => ({
      toasts: [...state.toasts, { id, message, tone, title, action }],
    }));
    return id;
  },
  dismiss: (id) => set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),
}));

/** Module-level helper — callable from anywhere (components, query cache, etc.). */
export const toast = {
  success: (message: string, title?: string, action?: ToastAction) =>
    useToastStore.getState().push(message, 'success', title, action),
  error: (message: string, title?: string, action?: ToastAction) =>
    useToastStore.getState().push(message, 'error', title, action),
  info: (message: string, title?: string, action?: ToastAction) =>
    useToastStore.getState().push(message, 'default', title, action),
  dismiss: (id: string) => useToastStore.getState().dismiss(id),
};
